/**
 * Orden canónico de `ruleflows.value` (igual que server/app/helpers/flowRuleflowOrderHelper.js).
 * Un send/return por flecha: send_to_next / return_to_previous. La policy decide persona o pool.
 */
import { isFlowRecordInAssignmentPool } from './flowEditPageHelper';
import { isDriDocumentPhase, resolveCasePhase } from './flowCasePhaseHelper';

const ALIAS = {
  reject_license: 'reject_record',
  cancel_record: 'reject_record',
  send_to_manager_pool: 'send_to_next',
  send_to_validator: 'send_to_next',
  send_to_verificator_pool: 'send_to_next',
  send_to_editor: 'send_to_next',
  send_to_editor_pool: 'send_to_next',
  return_to_pre: 'return_to_previous',
  return_to_pre_pool: 'return_to_previous',
  return_to_manager: 'return_to_previous',
  return_to_manager_pool: 'return_to_previous',
  return_to_validator: 'return_to_previous',
  return_to_validator_pool: 'return_to_previous'
};

const ORDER_FEEDER = ['send_to_next', 'claim_from_pool_pre'];
const ORDER_PRE = [
  'claim_from_feeder_pool',
  'return_to_previous',
  'mark_pending',
  'reject_record',
  'send_to_coordinator',
  'send_to_next',
  'claim_from_pool_manager'
];
const ORDER_COORDINATOR = [
  'return_to_previous',
  'mark_pending',
  'reject_record',
  'send_to_next',
  'pause_visibility',
  'resume_visibility',
  'decommission_record'
];
const ORDER_LIAISON = [
  'pause_visibility',
  'resume_visibility',
  'decommission_record'
];
const ORDER_MANAGER = [
  'claim_from_pre_pool',
  'claim_from_pool_verificator',
  'return_to_previous',
  'mark_pending',
  'reject_record',
  'send_to_next'
];
const ORDER_VALIDATOR = [
  'claim_from_pool_manager',
  'claim_from_pool_editor',
  'return_to_previous',
  'start_license_verification',
  'reject_record',
  'mark_pending',
  'end_license_verification',
  'send_to_next'
];
const ORDER_EDITOR = [
  'claim_from_pool_verificator',
  'return_to_previous',
  'start_expert_consult',
  'mark_pending',
  'reject_record',
  'end_expert_consult',
  'approve_publication'
];

export const FLOW_RULEFLOW_VALUE_ORDER = [
  ...ORDER_FEEDER,
  ...ORDER_PRE,
  ...ORDER_COORDINATOR,
  ...ORDER_LIAISON,
  ...ORDER_MANAGER,
  ...ORDER_VALIDATOR,
  ...ORDER_EDITOR
];

const LICENSE_RULES = new Set([
  'start_license_verification',
  'end_license_verification'
]);

const WAIT_START = new Set(['start_license_verification', 'start_expert_consult']);
const WAIT_END = new Set(['end_license_verification', 'end_expert_consult']);
const REJECT = new Set(['reject_record', 'reject_license', 'cancel_record']);
const FORWARD = new Set([
  'send_to_next',
  'send_to_coordinator',
  'send_to_manager_pool',
  'claim_from_pool_pre',
  'claim_from_feeder_pool',
  'claim_from_pre_pool',
  'claim_from_pool_manager',
  'claim_from_pool_verificator',
  'claim_from_pool_editor',
  'send_to_validator',
  'send_to_verificator_pool',
  'send_to_editor',
  'send_to_editor_pool',
  'approve_publication'
]);
const RETURN = new Set([
  'return_to_previous',
  'return_to_pre',
  'return_to_pre_pool',
  'return_to_manager',
  'return_to_manager_pool',
  'return_to_validator',
  'return_to_validator_pool'
]);
const CLAIM_RULES = new Set([
  'claim_from_pool_pre',
  'claim_from_feeder_pool',
  'claim_from_pre_pool',
  'claim_from_pool_manager',
  'claim_from_pool_verificator',
  'claim_from_pool_editor'
]);
const PUBLISHED_OPERATORS = new Set(['coordinator', 'liaison']);

const visibleClaimForPool = (roleKey = '', record = {}) => {
  const key = String(roleKey || '').toLowerCase();
  if (key === 'feeder') {
    return 'claim_from_pool_pre';
  }
  if (key === 'pre-manager') {
    return record?.origin_pre_manager_user_id
      ? 'claim_from_pool_manager'
      : 'claim_from_feeder_pool';
  }
  if (key === 'manager') {
    return record?.origin_verificator_user_id
      ? 'claim_from_pool_verificator'
      : 'claim_from_pre_pool';
  }
  if (key === 'validator' || key === 'verificator') {
    return record?.origin_editor_user_id
      ? 'claim_from_pool_editor'
      : 'claim_from_pool_manager';
  }
  if (key === 'editor') {
    return 'claim_from_pool_verificator';
  }
  return null;
};

const normalize = (value = '') => {
  const raw = String(value || '').trim().toLowerCase();
  return ALIAS[raw] || raw;
};

const pickOrder = (values) => {
  const set = values instanceof Set ? values : new Set(values);
  if (set.has('assign_to_pre_manager') && !set.has('send_to_next') && !set.has('send_to_manager_pool')) {
    return ORDER_FEEDER;
  }
  if (set.size === 1 && set.has('send_to_next')) {
    return ORDER_FEEDER;
  }
  if (set.has('claim_from_pool_pre') && !set.has('claim_from_feeder_pool') && !set.has('mark_pending')) {
    return ORDER_FEEDER;
  }
  if (set.has('claim_from_feeder_pool') || set.has('send_to_coordinator')) {
    return ORDER_PRE;
  }
  if (
    (set.has('pause_visibility') || set.has('resume_visibility') || set.has('decommission_record'))
    && !set.has('send_to_next')
    && !set.has('return_to_previous')
    && !set.has('send_to_manager_pool')
    && !set.has('return_to_pre')
  ) {
    return ORDER_LIAISON;
  }
  if (
    (set.has('send_to_next') || set.has('send_to_manager_pool'))
    && (set.has('return_to_previous') || set.has('return_to_pre'))
    && !set.has('claim_from_pre_pool')
    && !set.has('start_license_verification')
    && !set.has('approve_publication')
  ) {
    return ORDER_COORDINATOR;
  }
  if (set.has('send_to_manager_pool') || set.has('claim_from_pool_manager')) {
    return ORDER_PRE;
  }
  if (set.has('claim_from_pre_pool') || set.has('send_to_validator') || set.has('send_to_verificator_pool')) {
    return ORDER_MANAGER;
  }
  if (set.has('claim_from_pre_pool') || (set.has('send_to_next') && set.has('claim_from_pool_verificator') && !set.has('start_expert_consult'))) {
    return ORDER_MANAGER;
  }
  if (set.has('send_to_editor') || set.has('send_to_editor_pool') || set.has('start_license_verification')) {
    return ORDER_VALIDATOR;
  }
  if (set.has('start_license_verification') || (set.has('send_to_next') && set.has('end_license_verification'))) {
    return ORDER_VALIDATOR;
  }
  if (set.has('approve_publication') || set.has('start_expert_consult')) {
    return ORDER_EDITOR;
  }
  return FLOW_RULEFLOW_VALUE_ORDER;
};

const rankIn = (order, value) => {
  const idx = order.indexOf(normalize(value));
  return idx === -1 ? order.length : idx;
};

export const sortRuleflowsByCanonicalValue = (rows = []) => {
  const byFlow = new Map();
  rows.forEach((row) => {
    const fid = Number(row.flow_id) || 0;
    if (!byFlow.has(fid)) {
      byFlow.set(fid, []);
    }
    byFlow.get(fid).push(row);
  });
  const out = [];
  [...byFlow.keys()].sort((a, b) => a - b).forEach((fid) => {
    const group = byFlow.get(fid);
    const order = pickOrder(group.map((row) => normalize(row.value)));
    group.sort((left, right) => {
      const byKey = rankIn(order, left.value) - rankIn(order, right.value);
      if (byKey !== 0) {
        return byKey;
      }
      return (Number(left.id) || 0) - (Number(right.id) || 0);
    });
    out.push(...group);
  });
  return out;
};

export const hasActiveLicenseRules = (actions = []) =>
  (actions || []).some((action) => {
    if (action?.deleted) {
      return false;
    }
    return LICENSE_RULES.has(normalize(action.value));
  });

export const resolveStateName = (stateCatalogId, flowStates = []) => {
  const row = (flowStates || []).find((item) => Number(item.id) === Number(stateCatalogId));
  return row?.name || '';
};

export const isLicenseWaitState = (name = '') =>
  normalize(name) === 'en verificación de licencia' || normalize(name) === 'en verificacion de licencia';

export const isExpertWaitState = (name = '') => normalize(name) === 'en consulta a experto';

export const isForwardFlowAction = (action = {}) => FORWARD.has(normalize(action.value)) || FORWARD.has(String(action.value || '').trim().toLowerCase());
export const isReturnFlowAction = (action = {}) => RETURN.has(normalize(action.value)) || RETURN.has(String(action.value || '').trim().toLowerCase());
export const isSameTrayFlowAction = (action = {}) => {
  const value = normalize(action.value);
  return WAIT_START.has(value) || WAIT_END.has(value);
};
export const isTrayLeavingFlowAction = (action = {}) => {
  const value = normalize(action.value);
  const raw = String(action.value || '').trim().toLowerCase();
  return FORWARD.has(value) || FORWARD.has(raw) || RETURN.has(value) || RETURN.has(raw) || REJECT.has(value) || value === 'decommission_record';
};

export const hasCompletedLicenseVerification = (events = []) =>
  (events || []).some((event) => {
    const value = event?.rule?.value || event?.RuleFlow_To_FlowHistory?.value;
    return normalize(value) === 'end_license_verification';
  });

export const actionRequiresComment = (action = {}, noteForwardEnabled = false) => {
  const value = normalize(action.value);
  const raw = String(action.value || '').trim().toLowerCase();
  if (REJECT.has(value) || RETURN.has(value) || RETURN.has(raw) || value === 'start_expert_consult') {
    return true;
  }
  if (FORWARD.has(value) || FORWARD.has(raw)) {
    return !!noteForwardEnabled;
  }
  return !!action.requires_note;
};

export const filterVisibleFlowActions = ({
  actions = [],
  record,
  flowStates = [],
  moduleProcess,
  expertConsultEnabled = false,
  canOperate = true,
  licenseCompleted = false,
  roleKey = '',
  coordinatorApprovalEnabled = false,
  subjectKind = ''
} = {}) => {
  if (!canOperate) {
    return [];
  }

  const key = String(roleKey || '').toLowerCase();
  const isPublishedOperator = PUBLISHED_OPERATORS.has(key);
  const isVisible = record?.is_visible !== false;
  const stateName = resolveStateName(record?.state_catalog_id, flowStates);
  const publishedState = ['publicado', 'published', 'aprobado'].includes(normalize(stateName));
  const isPublished = Boolean(record?.published_at) || publishedState;

  if (isPublished) {
    if (!isPublishedOperator) {
      return [];
    }
    const publishedActions = (actions || []).filter((action) => {
      const value = normalize(action.value);
      if (!value || action.deleted) {
        return false;
      }
      if (value === 'pause_visibility') {
        return isVisible;
      }
      if (value === 'resume_visibility') {
        return !isVisible;
      }
      return value === 'decommission_record';
    });
    return sortRuleflowsByCanonicalValue(publishedActions);
  }

  const licenseWait = isLicenseWaitState(stateName);
  const expertWait = isExpertWaitState(stateName);
  const isLicenseModule = hasActiveLicenseRules(actions);

  const visible = (actions || []).filter((action) => {
    const raw = String(action.value || '').trim().toLowerCase();
    const value = normalize(action.value);
    if (!value || action.deleted) {
      return false;
    }
    if (subjectKind === 'search_key_term') {
      if (raw === 'send_to_next' && key !== 'feeder') {
        return false;
      }
      if ((REJECT.has(value) || raw === 'reject_record') && key !== 'feeder') {
        return false;
      }
      if (key === 'feeder' && record?.status && record.status !== 'draft') {
        return false;
      }
    }
    if (value === 'mark_pending') {
      return false;
    }
    if (value === 'pause_visibility' || value === 'resume_visibility') {
      return false;
    }
    if (value === 'decommission_record') {
      return isPublishedOperator;
    }
    const isAssignmentPool = isFlowRecordInAssignmentPool(record);
    if (isAssignmentPool) {
      const wanted = visibleClaimForPool(roleKey, record);
      return CLAIM_RULES.has(raw) && raw === wanted;
    }
    if (CLAIM_RULES.has(raw)) {
      return false;
    }
    if (raw === 'send_to_coordinator') {
      return key === 'pre-manager'
        && coordinatorApprovalEnabled
        && Boolean(record?.current_user_id);
    }
    if (value === 'send_to_next') {
      if (key === 'pre-manager' && coordinatorApprovalEnabled) {
        return false;
      }
      if (key === 'manager') {
        const phase = resolveCasePhase(record, {
          roleKey,
          process: moduleProcess,
          licenseCompleted
        });
        return Boolean(record?.current_user_id) && !isDriDocumentPhase(phase);
      }
      if ((key === 'validator' || key === 'verificator') && isLicenseModule) {
        return Boolean(record?.current_user_id) && licenseCompleted && !licenseWait;
      }
      return Boolean(record?.current_user_id);
    }
    if (REJECT.has(value)) {
      return isLicenseModule ? licenseWait : true;
    }
    if (WAIT_START.has(value) && value === 'start_license_verification') {
      return isLicenseModule && !licenseWait && !licenseCompleted;
    }
    if (WAIT_END.has(value) && value === 'end_license_verification') {
      return isLicenseModule && licenseWait;
    }
    if (value === 'start_expert_consult') {
      return expertConsultEnabled && !expertWait;
    }
    if (value === 'end_expert_consult') {
      return expertConsultEnabled && expertWait;
    }
    if (licenseWait && !['end_license_verification', 'reject_record', 'reject_license', 'cancel_record', 'return_to_previous', 'return_to_manager'].includes(raw) && value !== 'return_to_previous') {
      return false;
    }
    if (expertWait && !['end_expert_consult', 'return_to_previous', 'return_to_validator'].includes(raw) && value !== 'return_to_previous') {
      return false;
    }
    return true;
  });

  return sortRuleflowsByCanonicalValue(visible);
};

const flowActionVisibilityHelper = {
  filterVisibleFlowActions,
  sortRuleflowsByCanonicalValue,
  actionRequiresComment,
  isForwardFlowAction,
  isReturnFlowAction,
  isSameTrayFlowAction,
  isTrayLeavingFlowAction,
  hasCompletedLicenseVerification,
  hasActiveLicenseRules
};

export default flowActionVisibilityHelper;
